import { useState, useEffect, use } from 'react';
import useSWR, { useSWRConfig } from 'swr';

import { FOCUS_THROTTLE_INTERVAL } from '@/config/appSettings';

import { IResponseError } from '@/lib/apiBase';
import { ICommit, IRepository } from '@/types';
import { fetchCommitsBetween, IRepositoryCompareCommitsResponse } from '@/lib/repositoryCompareApi';

const useCommitsBetween = ({
  repository,
  initialData,
  base,
  head,
}: {
  repository: IRepository;
  initialData?: IRepositoryCompareCommitsResponse;
  base: string;
  head: string;
}) => {
  const [commits, setCommits] = useState<ICommit[] | undefined>(initialData?.compare?.commits);
  const [status, setStatus] = useState<'success' | 'error' | 'pending'>('pending');
  const [error, setError] = useState<IResponseError | undefined>();
  const fetchKey = [repository.namespace, repository.name, 'commits_between', base, head];

  const {
    data,
    mutate,
    error: requestError,
    isLoading,
    isValidating,
  } = useSWR(
    fetchKey,
    () =>
      fetchCommitsBetween({
        namespace: repository.namespace,
        repositoryName: repository.name,
        base: base,
        head: head,
        pageNumber: 1,
        pageSize: 100,
      }),
    {
      fallbackData: initialData,
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateOnMount: !initialData,
      revalidateOnReconnect: false,
      focusThrottleInterval: FOCUS_THROTTLE_INTERVAL,
    },
  );

  useEffect(() => {
    if (data) {
      const { status, error } = data;
      setCommits(data.compare?.commits || []);
      setStatus(data.status);
      setError(data.error as IResponseError);
    }
  }, [repository, data]);

  return { commits, status, error, isLoading, isValidating };
};

export default useCommitsBetween;
